import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, X, Send, Loader2, UserPlus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'dca_ai_chat_session';

/**
 * Floating AI assistant chat widget for the public site.
 * Brand-locked to business coaching topics (system prompt enforces this server-side).
 * Multi-turn via session_id stored in localStorage.
 * "Escalate to human" button emails the transcript to David.
 */
export default function AIAssistantWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || `chat_${Math.random().toString(36).slice(2, 12)}`; } catch { return `chat_${Math.random().toString(36).slice(2, 12)}`; }
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);
  const [escEmail, setEscEmail] = useState('');
  const [escName, setEscName] = useState('');
  const [escBusy, setEscBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, sessionId); } catch {/* ignore */} }, [sessionId]);
  useEffect(() => { if (open && messages.length === 0) loadHistory(); }, [open]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/api/ai/assistant/session/${sessionId}`);
      if (r.data?.messages?.length) setMessages(r.data.messages);
      else setMessages([{ role: 'assistant', text: "Hi! I'm DCA's AI assistant. I can help you understand David's business coaching programs, suggest your next step, or book a free 20-minute Jumpstart Session. What's on your mind?" }]);
    } catch { setMessages([{ role: 'assistant', text: 'Hi! How can I help with your business?' }]); }
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { role: 'user', text: msg }]);
    setInput(''); setBusy(true);
    try {
      const r = await axios.post(`${API}/api/ai/assistant/chat`, { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: 'assistant', text: r.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', text: "Sorry, I'm having trouble right now. Try refreshing, or click 'Escalate to David' below to get a real human response." }]);
    } finally { setBusy(false); }
  };

  const escalate = async () => {
    if (!escEmail.includes('@')) return toast.error('Please enter your email so David can reply');
    setEscBusy(true);
    try {
      await axios.post(`${API}/api/ai/assistant/escalate`, { session_id: sessionId, visitor_email: escEmail, visitor_name: escName, transcript: messages });
      toast.success("David will personally reply within 24 hours");
      setShowEscalate(false); setEscEmail(''); setEscName('');
    } catch (e) { toast.error('Could not send. Please email info@dcaenterprisesinc.com directly.'); } finally { setEscBusy(false); }
  };

  // Hide on admin/portal/auth pages
  if (typeof window !== 'undefined' && /^\/(admin|portal|login|signup|client|redeem)/i.test(window.location.pathname)) return null;

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed bottom-4 right-4 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white rounded-full w-14 h-14 shadow-2xl flex items-center justify-center z-40 transition" data-testid="ai-chat-open" aria-label="Open AI chat">
          <MessageCircle size={24}/>
        </button>
      )}
      {open && (
        <div className="fixed bottom-4 right-4 w-[360px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[calc(100vh-2rem)] bg-white rounded-2xl shadow-2xl border border-[#E2E8F0] flex flex-col z-40 overflow-hidden" data-testid="ai-chat-widget">
          <div className="bg-gradient-to-r from-[#0F172A] to-[#1D4ED8] text-white px-4 py-3 flex items-center justify-between">
            <div><div className="font-bold text-sm">DCA AI Assistant</div><div className="text-[11px] text-white/75">Powered by Claude · Replies instantly</div></div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white" data-testid="ai-chat-close"><X size={18}/></button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#F8FAFC]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${m.role === 'user' ? 'bg-[#1D4ED8] text-white' : 'bg-white border border-[#E2E8F0] text-[#0A0F1C]'}`}>{m.text}</div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="bg-white border border-[#E2E8F0] px-3 py-2 rounded-2xl text-sm text-[#94A3B8]"><Loader2 size={14} className="inline animate-spin mr-1.5"/>thinking...</div></div>}
          </div>
          {showEscalate ? (
            <div className="p-3 border-t border-[#E2E8F0] bg-white space-y-2" data-testid="ai-escalate-form">
              <div className="text-xs font-bold text-[#0A0F1C]">Get a personal reply from David</div>
              <input className="w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm" placeholder="Your name" value={escName} onChange={(e)=>setEscName(e.target.value)} data-testid="esc-name"/>
              <input className="w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm" placeholder="Your email *" value={escEmail} onChange={(e)=>setEscEmail(e.target.value)} data-testid="esc-email"/>
              <div className="flex gap-2">
                <button onClick={escalate} disabled={escBusy} className="btn-primary-orange text-xs flex-1" data-testid="esc-send">{escBusy?<><Loader2 size={12} className="animate-spin inline mr-1"/>Sending...</>:'Send transcript'}</button>
                <button onClick={()=>setShowEscalate(false)} className="btn-outline text-xs">Back</button>
              </div>
            </div>
          ) : (
            <div className="border-t border-[#E2E8F0] bg-white">
              <div className="flex items-center gap-2 p-3">
                <input value={input} onChange={(e)=>setInput(e.target.value)} onKeyDown={(e)=>e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())} placeholder="Type a question..." className="flex-1 px-3 py-2 border border-[#E2E8F0] rounded-full text-sm" disabled={busy} data-testid="ai-chat-input"/>
                <button onClick={send} disabled={busy || !input.trim()} className="bg-[#1D4ED8] hover:bg-[#1E40AF] disabled:bg-[#94A3B8] text-white rounded-full w-9 h-9 flex items-center justify-center transition" data-testid="ai-chat-send"><Send size={14}/></button>
              </div>
              {messages.length >= 2 && (
                <button onClick={()=>setShowEscalate(true)} className="w-full text-[11px] text-[#1D4ED8] hover:bg-[#F1F5F9] py-1.5 border-t border-[#F1F5F9] inline-flex items-center justify-center gap-1" data-testid="ai-escalate-btn">
                  <UserPlus size={11}/>Need a real human? Escalate to David
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
